import React, { useState } from "react";
import { Modal, Button, Row } from "react-bootstrap";
import * as yup from "yup";
import axios from "../../../../../service/axios";
import MerchantContacts from "./MerchantContacts";
import * as Constants from "../../../../../constants/Constants";
import { isMerchantEmailValid } from "../../../../../utils/utils";
import "./MerchantContactsModal.css";
import { useDispatch } from "react-redux";
import { updateServiceData } from "../../../../../redux/serviceSlice";

const MerchantContactsModal = ({
  open,
  toggleMerchantModal,
  merchantContact,
  multipleMerchantContactList,
  vendorProfile,
}) => {
  const [merchantContactList, setMerchantConatactList] = useState(
    multipleMerchantContactList
  );
  const [errors, setMerchantEmailValidation] = useState({});
  const [primaryRole, setPrimaryRole] = useState(merchantContact?.jobRole);
  const [disableSaveButton, setSaveButtonStatus] = useState(false);
  const dispatch = useDispatch();
  const handleClose = () => {
    setMerchantConatactList([]);
    toggleMerchantModal();
  };

  const handleCancel = () => {
    setMerchantConatactList(multipleMerchantContactList);
    setMerchantEmailValidation({});
    setPrimaryRole(merchantContact?.jobRole);
  };

  const handleSave = () => {
    let newMerchantContactList = [
      {
        ...merchantContact,
        jobRole: primaryRole,
      },
      ...merchantContactList,
    ].map((contact) => ({
      ...contact,
      duns: vendorProfile?.vendorDetail?.duns || "",
    }));

    setSaveButtonStatus(true);

    axios()
      .post("/contact/saveMultipleMerchantContacts", newMerchantContactList)
      .then((res) => {
        setSaveButtonStatus(false);
        dispatch(
          updateServiceData({
            merchantContact: newMerchantContactList[0],
            multipleMerchantContactList: [...res.data].length
              ? [...res.data].filter(
                  (contact) => contact.email !== merchantContact.email
                )
              : multipleMerchantContactList,
          })
        );
        toggleMerchantModal();
      })
      .catch((error) => {
        console.log(error);
        setSaveButtonStatus(false);
        toggleMerchantModal();
      });
  };

  const onDeleteMerchant = (merchantContact, index) => {
    const errorsObj = { ...errors };
    const checkFields = [
      `firstName${index}`,
      `lastName${index}`,
      `email${index}`,
      `jobRole${index}`,
    ];

    Object.keys(errorsObj).forEach((field) => {
      if (checkFields.includes(field)) {
        errorsObj[field] && delete errorsObj[field];

        setMerchantEmailValidation({ ...errorsObj });
      }
    });

    let merchantContactArr = [...merchantContactList];

    if (merchantContact.id !== null) {
      axios()
        .delete(`/contact/deleteContact/${merchantContact.id}`)
        .then((res) => {
          merchantContactArr.splice(index, 1);
          dispatch(
            updateServiceData({
              multipleMerchantContactList: [...merchantContactArr],
            })
          );
          setMerchantConatactList(merchantContactArr);
        })
        .catch((err) => console.log(err));
    } else {
      merchantContactArr.splice(index, 1);

      setMerchantConatactList(merchantContactArr);
    }
  };

  const onAddMerchantContact = () => {
    const {
      id = "",
      vendorCorpName = "",
      duns = "",
    } = vendorProfile?.vendorDetail;
    let merchantContactArr = [...merchantContactList];
    merchantContactArr.push({
      id: null,
      vendorProfileId: id,
      duns,
      vendorCorpName,
      firstName: "",
      lastName: "",
      email: "",
      userEmail: merchantContact?.userEmail,
      createdDate: null,
      updatedDate: null,
      primaryVendor: false,
      jobRole: "",
    });

    setMerchantConatactList(merchantContactArr);
  };

  const onChangeMerchantContact = (fieldName, event, merchantContactsIndex) => {
    event.preventDefault();
    if (merchantContactsIndex === -1) {
      setPrimaryRole(event.target.value);
    } else {
      let merchantContactArr = [...merchantContactList];
      const {
        id = "",
        vendorCorpName = "",
        duns = "",
      } = vendorProfile?.vendorDetail;

      merchantContactArr.splice(merchantContactsIndex, 1, {
        ...merchantContactArr[merchantContactsIndex],
        vendorProfileId: id,
        duns,
        vendorCorpName,
        primaryVendor: false,
        [fieldName]: event.target.value,
      });

      setMerchantConatactList(merchantContactArr);
    }
  };

  const validationSchema = yup.object({
    firstName: yup
      .string()
      .required("Field is required")
      .test((value) => {
        const val = value.replace(/  +/g, " ");
        const regex = /^([A-Za-z\u00C0-\u00D6\u00D8-\u00F6\u00F8-\u024F]?|[A-Za-z`~!@#$%^&*()=+\{\}\[\]\\|;:'",<.>//?_-\s])*$/;
        if (!regex.test(value) || val === " ") {
          return new yup.ValidationError(
            "The merchant first name may only contain letters, space, and special characters.",
            undefined,
            "firstName"
          );
        }
        return true;
      }),
    lastName: yup
      .string()
      .required("Field is required")
      .test((value) => {
        const val = value.replace(/  +/g, " ");
        const regex = /^([A-Za-z\u00C0-\u00D6\u00D8-\u00F6\u00F8-\u024F]?|[A-Za-z`~!@#$%^&*()=+\{\}\[\]\\|;:'",<.>//?_-\s])*$/;
        if (!regex.test(value) || val === " ") {
          return new yup.ValidationError(
            "The merchant last name may only contain letters, space, and special characters.",
            undefined,
            "lastName"
          );
        }
        return true;
      }),
    email: yup
      .string()
      .required("Field is required")
      .test((value) => {
        const fieldValue = value !== undefined && value?.split("@");
        const regFlag = isMerchantEmailValid(fieldValue[0]);
        const errFlag =
          fieldValue[1] === "macys.com" ||
          fieldValue[1] === "bloomingdales.com";

        if (!regFlag) {
          return new yup.ValidationError(
            Constants.VALID_EMAIL_ERROR_MSG,
            undefined,
            "email"
          );
        }
        if (!errFlag) {
          return new yup.ValidationError(
            Constants.MERCHANT_EMAIL_ERROR_MSG,
            undefined,
            "email"
          );
        }
        return true;
      }),
    jobRole: yup.string().required("Field is required"),
  });

  const handleValidation = (fieldKey, fieldId, fieldValue) => {
    yup
      .reach(validationSchema, `${[fieldId]}`)
      .validate(fieldValue)
      .then((value) => {
        let errorsObj = { ...errors };
        errorsObj[fieldKey] && delete errorsObj[fieldKey];

        setMerchantEmailValidation({ ...errorsObj });
      })
      .catch((err) => {
        let errorsObj = {};
        errorsObj[fieldKey] = err.message;

        setMerchantEmailValidation({ ...errors, ...errorsObj });
      });
  };

  const validateForm = () => {
    let validForm = false;
    const checkFields = ["firstName", "lastName", "email", "jobRole"];

    merchantContactList?.length > 0 &&
      merchantContactList.forEach((merchantContact) => {
        Object.keys(merchantContact).forEach((field) => {
          if (checkFields.includes(field)) {
            if (merchantContact[field] === "") {
              validForm = true;
            }
          }
        });
      });

    return validForm;
  };

  return (
    <>
      <Modal
        contentClassName="merchantContactsModal"
        show={open}
        onHide={handleClose}
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>{Constants.MANAGE_MERCHANT_CONTACTS_TITLE}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="merchantContactsModalBody">
          <Row>
            {merchantContact && (
              <MerchantContacts
                merchantContact={merchantContact}
                merchantContactsIndex={-1}
                errors={errors}
                handleValidate={handleValidation}
                primaryRole={primaryRole}
                onChangeMerchantContact={onChangeMerchantContact}
              />
            )}
          </Row>
          {merchantContactList &&
            merchantContactList.length > 0 &&
            merchantContactList.map((merchantContact, index) => {
              return (
                <Row key={`merchantContacts${index}`}>
                  <MerchantContacts
                    merchantContact={merchantContact}
                    merchantContactsIndex={index}
                    onChangeMerchantContact={onChangeMerchantContact}
                    errors={errors}
                    handleValidate={handleValidation}
                  />
                  {merchantContactList.length > 0 && (
                    <div
                      className="addMerchant"
                      onClick={() => onDeleteMerchant(merchantContact, index)}
                      style={{ marginLeft: "auto" }}
                    >
                      {Constants.DELETE_MERCHANT}
                    </div>
                  )}
                </Row>
              );
            })}
          {merchantContactList && merchantContactList.length < 4 && (
            <Row>
              <div
                className="addMerchant floatRight"
                onClick={() => onAddMerchantContact()}
              >
                {Constants.ADD_OTHER_MERCHANT}
              </div>
            </Row>
          )}
        </Modal.Body>
        <Modal.Footer className="merchantContactsFooter">
          <Button className="button-later-md" onClick={handleCancel}>
            {Constants.BUTTON_CANCEL}
          </Button>
          <Button
            className="button-primary-md"
            onClick={handleSave}
            disabled={
              !(
                Object.entries(errors).length === 0 &&
                errors.constructor === Object
              ) ||
              !primaryRole ||
              validateForm() ||
              disableSaveButton
            }
          >
            {Constants.BUTTON_SAVE}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default MerchantContactsModal;
